import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiDocumentText,
  HiBuildingOffice2,
  HiCalendar,
  HiIdentification,
  HiClipboardDocumentList,
  HiCurrencyDollar,
  HiDocumentCheck,
  HiHashtag,
  HiBuildingStorefront,
  HiClock,
  HiBanknotes,
  HiUserGroup,
  HiVideoCamera,
  HiPencilSquare,
  HiArrowLeft,
  HiArrowRight,
  HiXMark,
  HiUser,
} from 'react-icons/hi2';
import {
  uploadFile,
  STORAGE_PATHS,
  createApplication,
  updateApplication,
} from '../../database/firebase';
import toast from 'react-hot-toast';
const steps = [
  { id: 'documents', title: 'Required Documents', icon: HiDocumentText },
  { id: 'business', title: 'Business Information', icon: HiBuildingOffice2 },
  { id: 'interview', title: 'Interview Preferences', icon: HiCalendar },
];

const documentIcons = {
  idDocument: HiIdentification,
  businessPlan: HiClipboardDocumentList,
  financialStatements: HiCurrencyDollar,
  taxClearance: HiDocumentCheck,
};

const businessIcons = {
  businessName: HiBuildingOffice2,
  registrationNumber: HiHashtag,
  industry: HiBuildingStorefront,
  yearsInOperation: HiClock,
  annualRevenue: HiBanknotes,
  employeeCount: HiUserGroup,
  location: HiUser,
  organization: HiBuildingOffice2,
};

const containerVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

const stepVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: 0.2,
      ease: 'easeIn',
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.02,
    transition: {
      duration: 0.2,
    },
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: 0.1,
    },
  },
};

const ApplicationForm = ({ applicantId, clientId, userId, userEmail, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    documents: {
      idDocument: null,
      businessPlan: null,
      financialStatements: null,
      taxClearance: null,
    },
    business: {
      businessName: '',
      registrationNumber: '',
      industry: '',
      yearsInOperation: '',
      annualRevenue: '',
      employeeCount: '',
      location: '',
      organization: '',
    },
    interview: {
      preferredDate: '',
      preferredTime: '',
      interviewType: 'virtual',
      additionalNotes: '',
    },
  });

  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const initializeApplication = async () => {
      try {
        setApplicationId(applicantId);
      } catch (error) {
        toast.error('Error initializing application:', error);
        setErrors(prev => ({ ...prev, initialization: 'Failed to initialize application' }));
      }
    };

    if (applicantId && !applicationId) {
      initializeApplication();
    }
  }, [applicantId, applicationId]);

  const handleFileUpload = async (field, file) => {
    try {
      setUploadProgress(prev => ({ ...prev, [field]: 0 }));
      setLoading(true);

      const path = `${userId}/${STORAGE_PATHS[field.toUpperCase()]}`;
      const downloadURL = await uploadFile(file, path);

      setFormData(prev => ({
        ...prev,
        documents: { ...prev.documents, [field]: { name: file.name, url: downloadURL } },
      }));

      if (applicationId) {
        await updateApplication(applicationId, {
          [`documents.${field}`]: { name: file.name, url: downloadURL },
          updatedAt: new Date().toISOString(),
        });
      }

      setUploadProgress(prev => ({ ...prev, [field]: 100 }));
    } catch (error) {
      toast.error('Error uploading file:', error);
      setErrors(prev => ({ ...prev, [field]: 'Failed to upload file' }));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = async (section, field, value) => {
    try {
      setFormData(prev => ({
        ...prev,
        [section]:
          section === 'additional'
            ? { ...prev[section], [field]: value }
            : { ...prev[section], [field]: value },
      }));

      if (applicationId) {
        if (section === 'additional') {
          await updateApplication(applicationId, {
            [field]: value,
            updatedAt: new Date().toISOString(),
          });
        } else {
          await updateApplication(applicationId, {
            [section]: { ...formData[section], [field]: value },
            updatedAt: new Date().toISOString(),
          });
        }
        showToast(`${field} updated successfully!`, 'success');
      }
    } catch (error) {
      toast.error('Error updating field:', error);
      setErrors(prev => ({ ...prev, [field]: 'Failed to update field' }));
      showToast(`Failed to update ${field}`, 'error');
    }
  };

  const validateStep = () => {
    const currentStepData = formData[steps[currentStep].id];
    const newErrors = {};

    if (steps[currentStep].id === 'documents') {
      Object.keys(currentStepData).forEach(key => {
        if (!currentStepData[key]?.url) {
          newErrors[key] = 'This document is required';
        }
      });
    } else if (steps[currentStep].id === 'business') {
      if (!formData.business.location) {
        newErrors.location = 'Location is required';
      }
      if (!formData.business.organization) {
        newErrors.organization = 'Organization is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (validateStep()) {
      try {
        setLoading(true);

        if (currentStep < steps.length - 1) {
          if (applicationId) {
            await updateApplication(applicationId, {
              currentStep: currentStep + 1,
              updatedAt: new Date().toISOString(),
            });
          }
          setCurrentStep(prev => prev + 1);
        } else {
          if (applicationId) {
            await updateApplication(applicationId, {
              status: 'completed',
              completedAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          }
          onComplete(formData);
        }
      } catch (error) {
        toast.error('Error updating step:', error);
        setErrors(prev => ({ ...prev, step: 'Failed to update step' }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = async () => {
    try {
      setLoading(true);
      if (applicationId) {
        await updateApplication(applicationId, {
          currentStep: currentStep - 1,
          updatedAt: new Date().toISOString(),
        });
      }
      setCurrentStep(prev => prev - 1);
    } catch (error) {
      toast.error('Error going back:', error);
      setErrors(prev => ({ ...prev, step: 'Failed to go back' }));
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (steps[currentStep].id) {
      case 'documents':
        return (
          <div className="space-y-6">
            {Object.entries(formData.documents).map(([key, value]) => {
              const Icon = documentIcons[key];
              return (
                <div key={key} className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Icon className="h-5 w-5 text-blue-500" />
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      onChange={e => handleFileUpload(key, e.target.files[0])}
                      className="hidden"
                      id={key}
                      accept=".pdf,.doc,.docx"
                    />
                    <label
                      htmlFor={key}
                      className="group flex cursor-pointer items-center justify-between rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-4 transition-all duration-200 hover:border-blue-500 hover:bg-blue-50/50"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white text-blue-500 shadow-sm transition-all duration-200 group-hover:scale-110">
                          <Icon className="h-6 w-6" />
                        </div>
                        <span className="text-sm text-gray-600">
                          {value ? value.name : 'Click to upload file'}
                        </span>
                      </div>
                      {uploadProgress[key] > 0 && (
                        <div className="w-24">
                          <div className="h-2 rounded-full bg-gray-200">
                            <div
                              className="h-2 rounded-full bg-blue-500 transition-all duration-300"
                              style={{ width: `${uploadProgress[key]}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </label>
                    {errors[key] && (
                      <p className="mt-1 flex items-center gap-1 text-sm text-red-600">
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        {errors[key]}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'business':
        return (
          <div className="space-y-6">
            {Object.entries(formData.business).map(([key, value]) => {
              const Icon = businessIcons[key] || HiBuildingOffice2;
              return (
                <div key={key} className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Icon className="h-5 w-5 text-blue-500" />
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={value}
                      onChange={e => handleInputChange('business', key, e.target.value)}
                      className="w-full rounded-xl border border-gray-300 bg-gray-50/50 px-4 py-3 pl-12 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                      placeholder={`Enter your ${key.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
                    />
                    <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 transform text-gray-400">
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'interview':
        return (
          <div className="space-y-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <HiCalendar className="h-5 w-5 text-blue-500" />
                Preferred Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={formData.interview.preferredDate}
                  onChange={e => handleInputChange('interview', 'preferredDate', e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50/50 px-4 py-3 pl-12 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 transform text-gray-400">
                  <HiCalendar className="h-5 w-5" />
                </div>
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <HiClock className="h-5 w-5 text-blue-500" />
                Preferred Time
              </label>
              <div className="relative">
                <input
                  type="time"
                  value={formData.interview.preferredTime}
                  onChange={e => handleInputChange('interview', 'preferredTime', e.target.value)}
                  className="w-full rounded-xl border border-gray-300 bg-gray-50/50 px-4 py-3 pl-12 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
                <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 transform text-gray-400">
                  <HiClock className="h-5 w-5" />
                </div>
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <HiVideoCamera className="h-5 w-5 text-blue-500" />
                Interview Type
              </label>
              <div className="relative">
                <select
                  value={formData.interview.interviewType}
                  onChange={e => handleInputChange('interview', 'interviewType', e.target.value)}
                  className="w-full appearance-none rounded-xl border border-gray-300 bg-gray-50/50 px-4 py-3 pl-12 text-gray-900 transition-all duration-200 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <option value="virtual">Virtual</option>
                  <option value="in-person">In-Person</option>
                </select>
                <div className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 transform text-gray-400">
                  <HiVideoCamera className="h-5 w-5" />
                </div>
                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 transform text-gray-400">
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <HiPencilSquare className="h-5 w-5 text-blue-500" />
                Additional Notes
              </label>
              <div className="relative">
                <textarea
                  value={formData.interview.additionalNotes}
                  onChange={e => handleInputChange('interview', 'additionalNotes', e.target.value)}
                  rows="4"
                  className="w-full rounded-xl border border-gray-300 bg-gray-50/50 px-4 py-3 pl-12 text-gray-900 placeholder-gray-500 transition-all duration-200 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Add any additional notes or requirements..."
                />
                <div className="pointer-events-none absolute left-4 top-4 transform text-gray-400">
                  <HiPencilSquare className="h-5 w-5" />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
    >
      <motion.div className="relative w-[92%] max-w-2xl rounded-2xl bg-white p-4 shadow-2xl sm:w-full md:p-8">
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  <motion.div
                    className="flex flex-col items-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <motion.div
                      className={`flex h-10 w-10 items-center justify-center rounded-full text-lg transition-all duration-200 md:h-12 md:w-12 md:text-xl ${
                        index <= currentStep
                          ? 'bg-blue-500 text-white shadow-lg shadow-blue-200'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Icon className="h-6 w-6" />
                    </motion.div>
                    <motion.span
                      className="mt-2 text-xs font-medium text-gray-600 md:text-sm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.1 + 0.2 }}
                    >
                      {step.title}
                    </motion.span>
                  </motion.div>
                  {index < steps.length - 1 && (
                    <motion.div
                      className={`hidden h-1 w-24 md:block ${index < currentStep ? 'bg-blue-500' : 'bg-gray-200'}`}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: index < currentStep ? 1 : 0 }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            variants={stepVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="mb-8"
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <motion.button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2.5 text-gray-700 transition-all duration-200 hover:bg-gray-50 disabled:opacity-50 md:px-6 md:py-3"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <HiArrowLeft className="h-5 w-5" />
            Back
          </motion.button>
          <motion.button
            onClick={handleNext}
            className="flex items-center gap-2 rounded-xl bg-blue-500 px-4 py-2.5 text-white transition-all duration-200 hover:bg-blue-600 md:px-6 md:py-3"
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            {currentStep === steps.length - 1 ? 'Submit Application' : 'Next'}
            {currentStep < steps.length - 1 && <HiArrowRight className="h-5 w-5" />}
          </motion.button>
        </div>
        <motion.button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600"
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
        >
          <HiXMark className="h-6 w-6" />
        </motion.button>

        <AnimatePresence>
          {toast && (
            <motion.div
              initial={{ opacity: 0, y: -50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.95 }}
              className={`fixed right-4 top-4 z-50 rounded-lg px-4 py-3 shadow-lg ${
                toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                {toast.type === 'success' ? (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
                {toast.message}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default ApplicationForm;
